/**
 * LeftRail Component
 * LAYER-SHELL: Unified desktop sidebar navigation
 *
 * Desktop sidebar navigation with collapsible rail.
 * Uses useNavigation hook for role-based nav item filtering.
 * Shows ALL navigation items the user's role can access.
 */
import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, EyeIcon } from '@heroicons/react/24/outline';
import { NavItem } from './NavItem';
import { useNavigation } from '../../hooks/useNavigation';
import type { UserRole } from './Shell';
import './LeftRail.scss';

interface LeftRailProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  userRole: UserRole;
  canViewAsCustomer?: boolean;
  viewAsCustomer?: boolean;
  onToggleViewAsCustomer?: () => void;
}

export const LeftRail: React.FC<LeftRailProps> = ({
  isCollapsed,
  onToggleCollapse,
  userRole,
  canViewAsCustomer,
  viewAsCustomer,
  onToggleViewAsCustomer,
}) => {
  const navItems = useNavigation(userRole);

  return (
    <aside
      className={`left-rail ${isCollapsed ? 'left-rail--collapsed' : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Customer view mode banner */}
      {viewAsCustomer && !isCollapsed && (
        <div className="left-rail__customer-banner">
          Customer View
        </div>
      )}

      <nav className="left-rail__nav">
        <ul className="left-rail__list">
          {navItems.map((item) => (
            <li key={item.id}>
              <NavItem
                path={item.path}
                label={item.label}
                icon={item.icon}
                isCollapsed={isCollapsed}
              />
            </li>
          ))}
        </ul>
      </nav>

      <div className="left-rail__footer">
        {canViewAsCustomer && onToggleViewAsCustomer && (
          <button
            className={`left-rail__view-toggle ${viewAsCustomer ? 'left-rail__view-toggle--active' : ''}`}
            onClick={onToggleViewAsCustomer}
            aria-label={viewAsCustomer ? 'Exit customer view' : 'View as customer'}
            title={viewAsCustomer ? 'Exit customer view' : 'View as customer'}
          >
            <EyeIcon className="left-rail__view-toggle-icon" />
            {!isCollapsed && (
              <span className="left-rail__view-toggle-label">
                {viewAsCustomer ? 'Exit Customer View' : 'View as Customer'}
              </span>
            )}
          </button>
        )}
        <button
          className="left-rail__collapse-btn"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? (
            <ChevronRightIcon className="left-rail__collapse-icon" />
          ) : (
            <ChevronLeftIcon className="left-rail__collapse-icon" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default LeftRail;
